import {
  Component,
  Input,
  ViewChild,
  OnInit,
  AfterViewInit,
  HostListener,
} from '@angular/core';
import { TruckSearch } from '@app/trucks/models/truck-search.model';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AppService } from '@app/app.service';
import { TruckSearchFiltersDialogComponent } from '@app/trucks/truck-search-filters-dialog/truck-search-filters-dialog.component';
import { TruckViewPublicDialogComponent } from '@app/trucks/truck-view-public-dialog/truck-view-public-dialog.component';
import { DatePipe } from '@angular/common';
import { AuthService } from '@app/auth/auth.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { NumberFilterDialogComponent } from '@app/shared/number-filter-dialog/number-filter-dialog.component';
import { LocationFilterDialogComponent } from '@app/shared/location-filter-dialog/location-filter-dialog.component';
import { MatChipsModule } from '@angular/material/chips';
import { MatMenuModule } from '@angular/material/menu';
import { DateFilterDialogComponent } from '@app/shared/date-filter-dialog/date-filter-dialog.component';
import { UserPlan } from '@app/auth/user.model';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';

interface FilterChip {
  key: string;
  label: string;
  action: () => void;
  removable: boolean;
}

@Component({
  selector: 'app-truck-search',
  imports: [
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatIconModule,
    DatePipe,
    MatTooltipModule,
    CommonModule,
    MatChipsModule,
    MatMenuModule,
    MatDividerModule,
  ],
  templateUrl: './truck-search.component.html',
  styleUrl: './truck-search.component.scss',
})
export class TruckSearchComponent implements OnInit, AfterViewInit {
  searchId: number | null = null;
  
  @Input() search: TruckSearch | null = null;
  @Input() active = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = false;

  results = new MatTableDataSource<any>([]);

  minSearchRadius = 50;
  maxSearchRadius = 500;

  private autoRefreshEnabled = true;
  private refreshTimer: any;
  private defaultRefreshInterval = 120; // in seconds, will be set from user plan settings
  private refreshInterval: number; // in milliseconds, will be set from user plan settings

  private defaultTimer: ReturnType<typeof setTimeout> | null = null;
  // Delay before setting the search as default after user stops switching tabs. 
  // This is to avoid setting the default when user is actively switching tabs.
  private setDefaultSearchAfter = 10_000; // 10 seconds

  private manualRefreshLimit = 30_000; // 30 seconds
  private lastManualRefreshAt: number | null = null;

  userPlan: UserPlan | null = null;

  isMobile = window.innerWidth < 768;
  itemsPerPage = this.isMobile ? 10 : 25;

  desktopColumns: string[] = [
    'is_saved',
    'age',
    'available_dates',
    'origin_deadhead_distance',
    'origin_city',
    'origin_state',
    'max_weight',
    'axle_count',
    'well_length',
    'carrier_base_state',
    'carrier',
  ];

  mobileColumns = [
    'mobile'
  ];

  displayedColumns = this.isMobile ? this.mobileColumns : this.desktopColumns;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
    this.displayedColumns = this.isMobile
      ? this.mobileColumns
      : this.desktopColumns;
    
    // If the search is not set and it's not mobile, navigate back to the searches list
    if (!this.search && !this.isMobile) {
      this.goBack();
    }
  }

  filterChips: FilterChip[] = [];

  allAvailableFilters: { key: string; label: string }[] = [
    { key: 'available_dates', label: 'Available Dates' },
    { key: 'min_weight', label: 'Can Scale' },
    { key: 'min_axle_count', label: 'Min Axles' },
    { key: 'min_well_length', label: 'Min Well Length' },
  ];

  availableFilters: { key: string; label: string }[] = [];

  constructor(
    private dialog: MatDialog,
    private appService: AppService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) { 
    // Setting up auto-trefresh interval based on user plan settings
    this.userPlan = this.authService.getUserPlanFromStorage();
    const refreshSeconds = this.userPlan?.search_auto_refresh_interval_seconds ?? this.defaultRefreshInterval;
    this.refreshInterval = refreshSeconds * 1000; // convert to milliseconds

    // Setting max search radius based on user plan settings
    this.maxSearchRadius = this.userPlan?.max_search_radius_miles ?? this.maxSearchRadius;
  }

  ngOnInit(): void {
    if (!this.authService.userCanSearchTrucks()) {
      this.appService.navigateToDefaultUrl();
      return;
    }

    if (this.search) {
      this.runSearch();
      this.updateFilterUi();
      return;
    }

    // URL by id should only be used on mobile
    if (!this.isMobile) {
      this.goBack();
      return;
    }

    this.searchId = Number(this.route.snapshot.paramMap.get('id'));

    if (!this.searchId) {
      this.appService.errorSnackBar('Search id is missing.');
      return;
    }

    this.getSearch();
  }

  ngAfterViewInit(): void {
    this.results.paginator = this.paginator;
    this.results.sort = this.sort;

    this.results.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {

        case 'is_saved':
          return item.is_saved ? 1 : 0;

        case 'age':
          return new Date(item.refreshed_at).getTime();

        case 'origin_deadhead_distance':
          return item.origin_deadhead_distance ?? 999999;

        case 'origin_city':
          return item.origin_city ?? '';

        case 'origin_state':
          return item.origin_state ?? '';

        case 'available_dates':
          return item.available_start_date ?? '';

        case 'carrier':
          return item.carrier_name ?? '';

        case 'carrier_base_state':
          return item.carrier_base_state ?? '';

        default:
          return item[property];
      }
    };
  }

  ngOnChanges(): void {
    if (this.active) {
      this.startDefaultTimer();

      if (this.autoRefreshEnabled) {
        this.startAutoRefresh();
      }
    } else {
      this.stopDefaultTimer();
      this.stopAutoRefresh();
    }
  }

  ngOnDestroy(): void {
    this.stopDefaultTimer();
    this.stopAutoRefresh();
  }

  getSearch(): void {
    if (!this.searchId) {
      return;
    }

    this.appService.get(`trucks/searches/${this.searchId}`).subscribe({
      next: (res) => {
        this.search = res.data;
        this.runSearch();
        this.updateFilterUi();
      },
      error: (err) => {
        this.appService.handleError(err);
        this.goBack();
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/trucks/searches']);
  }

  private startAutoRefresh(): void {
    if (this.refreshTimer) {
      return;
    }

    this.refreshTimer = setInterval(() => {
      if (!this.loading) {
        this.runSearch();
      }
    }, this.refreshInterval);
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private resetAutoRefresh(): void {
    if (this.autoRefreshEnabled) {
      this.stopAutoRefresh();
      this.startAutoRefresh();
    }
  }

  updateSearch(): void {
    const dialogRef = this.dialog.open(TruckSearchFiltersDialogComponent, {
      width: '550px',
      maxWidth: '95vw',
      data: { search: this.search },
    });

    dialogRef.afterClosed().subscribe((result: TruckSearch | undefined) => {
      if (!result) return;

      this.search = result;
      this.runSearch();
      this.updateFilterUi();
    });
  }

  runSearch(resultMessage: boolean = false, manual: boolean = false): void {

    if (!this.search) {
      return;
    }

    // Manual refresh rate limit
    if (manual && this.lastManualRefreshAt) {
      const diff = Date.now() - this.lastManualRefreshAt;

      if (diff < this.manualRefreshLimit) {
        const waitSeconds = Math.ceil((this.manualRefreshLimit - diff) / 1000);
        this.appService.infoSnackBar(`You can refresh again in ${waitSeconds} seconds.`,);
        return;
      }
    }

    if (manual) {
      this.lastManualRefreshAt = Date.now();
    }

    this.loading = true;
    this.resetAutoRefresh();

    this.appService
      .get(`trucks/searches/${this.search.id}/results`)
      .subscribe({
        next: (res) => {

          const newData = res.data ?? [];
          const changes = this.getResultChanges(newData);

          this.results.data = newData;
          this.results.paginator = this.paginator;
          this.results.sort = this.sort;

          if (res.time < 0.5) {
            // Ensure spinner shows for at least 500ms to prevent flickering
            setTimeout(() => {
              this.loading = false;
            }, 500);
          } else {
            this.loading = false;
          }

          if (resultMessage) {

            if (res.data?.length === 0) {
              this.appService.showSnackBar(
                `No trucks found`,
                'warning'
              );
            } else {
              this.appService.showSnackBar(
                `${res.data?.length ?? 0} trucks found`,
                'info'
              );
            }
          }
        },
        error: (err) => {
          this.appService.showSnackBar(err.error.message, 'error');
        },
      });
  }

  private getResultChanges(newData: any[]) {
    const oldIds = new Set(this.results.data.map(load => load.id));
    const newIds = new Set(newData.map(load => load.id));
    return {
      added: [...newIds].filter(id => !oldIds.has(id)).length,
      removed: [...oldIds].filter(id => !newIds.has(id)).length,
    };
  }

  viewTruckDetails(truck: any): void {
    this.dialog.open(TruckViewPublicDialogComponent, {
      width: '900px',
      maxWidth: '900px',
      data: {
        truck: truck,
        toggleSavedTruck: () => {
          this.toggleSavedTruck(truck);
        }
      },
    });
  }

  toggleSavedTruck(truck: any): void {
    const originalValue = truck.is_saved;

    truck.is_saved = !originalValue;

    this.appService
      .patch(`saved/trucks/${truck.id}/toggle-saved`, {})
      .subscribe({
        next: (res) => {
          truck.is_saved = res.data.is_saved;
          this.sortFavoritesOnTop();
        },
        error: (err) => {
          truck.is_saved = originalValue;
          this.appService.handleError(err);
        },
      });
  }

  isFresh(item: any): boolean {
    if (!item.refreshed_since) {
      return false;
    }

    const [hours, minutes] = item.refreshed_since.split(':').map(Number);

    return hours === 0 && minutes < 5;
  }

  toFeetAndInches(totalInches: number): string {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

  private sortFavoritesOnTop(): void {
    this.results.data = [...this.results.data].sort((a, b) => {
      const savedCompare = Number(b.is_saved) - Number(a.is_saved);

      if (savedCompare !== 0) {
        return savedCompare;
      }

      return new Date(b.refreshed_at).getTime() - new Date(a.refreshed_at).getTime();
    });
  }

  formatAge(refreshedSince: string): string {
    if (!refreshedSince) {
      return '';
    }

    const [hours, minutes] = refreshedSince.split(':').map(Number);

    if (hours < 24) {
      return refreshedSince;
    }

    const days = Math.floor(hours / 24);

    return `${days} day${days > 1 ? 's' : ''}`;
  }

  // Set Default Tab Logic
  // Implemeting timer to avoid refreshing default when user
  // is actively switching the tabs.
  private startDefaultTimer(): void {
    this.stopDefaultTimer();

    this.defaultTimer = setTimeout(() => {
      this.defaultTimer = null;
      this.setDefault();
    }, this.setDefaultSearchAfter);
  }

  private stopDefaultTimer(): void {
    if (this.defaultTimer) {
      clearTimeout(this.defaultTimer);
      this.defaultTimer = null;
    }
  }

  private setDefault(): void {

    if (!this.search || this.search.is_default) {
      return;
    }

    const search = this.search;

    this.appService
      .patch(`trucks/searches/${this.search.id}/default`, {})
      .subscribe({
        next: () => {
          search.is_default = true;
        },
        error: (err) => {
          console.error('Failed to set default search:', err);
        },
      });
  }


  getFilterChips(): FilterChip[] {

    if (!this.search) {
      return [];
    }

    const filters = this.search.filters;
    const chips: FilterChip[] = [];

    const add = (
      key: string,
      label: string,
      action: () => void = () => { },
      removable: boolean = true
    ) => {
      chips.push({
        key,
        label,
        action,
        removable,
      });
    };

    if (filters.origin) {
      add(
        'origin',
        `From: ${this.formatLocationLabel(filters.origin)}`,
        () => this.openLocationFilterDialog('origin', 'Origin'),
        false
      );
    }

    if (filters.origin_radius) {
      add(
        'origin_radius',
        `${filters.origin_radius} mi`,
        () => this.openNumberFilterDialog('origin_radius', 'Origin Radius', 50, this.minSearchRadius, this.maxSearchRadius, 'mi'),
        false,

      );
    }

    if (filters.available_start_date || filters.available_end_date) {
      add(
        'available_dates',
        `Available: ${this.formatDateChip(filters.available_start_date, filters.available_end_date)}`,
        () => this.openDateRangeFilterDialog(
          'available_start_date',
          'available_end_date',
          'Available Dates'
        ),
        true
      );
    }

    if (filters.min_weight) {
      add(
        'min_weight',
        `Can Scale: ${filters.min_weight.toLocaleString()} lbs`,
        () => this.openNumberFilterDialog('min_weight', 'Can Scale', 1000, 1, 200000, 'lbs'),
        true
      );
    }

    if (filters.min_axle_count) {
      add(
        'min_axle_count',
        `Min Axles: ${filters.min_axle_count}`,
        () => this.openNumberFilterDialog('min_axle_count', 'Min Axles', 1, 1, 20),
        true
      );
    }

    if (filters.min_well_length) {
      add(
        'min_well_length',
        `Min Well Length: ${filters.min_well_length} in`,
        () => this.openNumberFilterDialog('min_well_length', 'Min Well Length', 1, 120, 1000, 'in'),
        true
      );
    }

    return chips;
  }

  removeFilter(key: string): void {
    if (!this.search) {
      return;
    }

    delete (this.search.filters as any)[key];

    if (key === 'available_dates') {
      delete (this.search.filters as any)['available_start_date'];
      delete (this.search.filters as any)['available_end_date'];
    }

    this.updateFilterUi();
    this.updateSearchFilters();
  }

  openNumberFilterDialog(
    key: string,
    label: string,
    step: number,
    min_value: number | null = null,
    max_value: number | null = null,
    suffix: string = ''
  ): void {

    if (!this.search) {
      return;
    }

    const value = (this.search.filters as any)[key];

    const dialogRef = this.dialog.open(NumberFilterDialogComponent, {
      data: {
        label,
        value,
        step,
        min_value,
        max_value,
        suffix,
      },
    });

    dialogRef.afterClosed().subscribe((value) => {
      if (!this.search || value === undefined) {
        return;
      }

      (this.search.filters as any)[key] = value;

      this.updateFilterUi();
      this.updateSearchFilters();
    });
  }

  openLocationFilterDialog(
    key: string,
    label: string
  ): void {
    if (!this.search) {
      return;
    }

    const radiusKey = `${key}_radius`;

    const dialogRef = this.dialog.open(LocationFilterDialogComponent, {
      width: '450px',
      data: {
        label,
        value: (this.search.filters as any)[key] ?? '',
        latitude: (this.search.filters as any)[`${key}_latitude`] ?? null,
        longitude: (this.search.filters as any)[`${key}_longitude`] ?? null,
        radius: (this.search.filters as any)[radiusKey] ?? 250,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!this.search || !result) {
        return;
      }

      (this.search.filters as any)[key] = result.location;
      (this.search.filters as any)[`${key}_latitude`] = result.latitude;
      (this.search.filters as any)[`${key}_longitude`] = result.longitude;
      (this.search.filters as any)[radiusKey] = result.radius;

      this.updateFilterUi();
      this.updateSearchFilters();
    });
  }

  private formatLocationLabel(location: string): string {
    const withoutZip = location.replace(/\s*\b\d{5}\b\s*/g, '').trim();

    const [city = '', state = ''] = withoutZip.split(',');

    const formattedCity = city
      .trim()
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return state
      ? `${formattedCity}, ${state.trim().toUpperCase()}`
      : formattedCity;
  }

  updateSearchFilters(): void {

    if (!this.search) {
      return;
    }

    this.appService
      .patch(`trucks/searches/${this.search.id}`, this.search.filters)
      .subscribe({
        next: () => {
          this.runSearch();
        },
        error: (err) => {
          this.appService.handleError(err);
        },
      });
  }

  updateAvailableFilters(): void {
    if (!this.search) {
      return;
    }

    const filters = this.search.filters as any;

    this.availableFilters = this.allAvailableFilters.filter((filter) => {
      if (filter.key === 'available_dates') {
        return !filters.available_start_date && !filters.available_end_date;
      }

      return !(filter.key in filters);
    });
  }

  updateFilterUi(): void {
    this.filterChips = this.getFilterChips();
    this.updateAvailableFilters();
  }

  addFilter(key: string): void {
    switch (key) {

    case 'available_dates':
      this.openDateRangeFilterDialog(
        'available_start_date',
        'available_end_date',
        'Available Dates'
      );

      break;
      case 'min_weight':
        this.openNumberFilterDialog('min_weight', 'Can Scale', 1000, 1, 200000, 'lbs');
        break;

      case 'min_axle_count':
        this.openNumberFilterDialog('min_axle_count', 'Min Axles', 1, 1, 20);
        break;

      case 'min_well_length':
        this.openNumberFilterDialog('min_well_length', 'Min Well Length', 1, 120, 1000, 'in');
        break;
    }
  }

  openDateRangeFilterDialog(
    startKey: string,
    endKey: string,
    label: string
  ): void {
    
    if (!this.search) {
      return;
    }

    const dialogRef = this.dialog.open(DateFilterDialogComponent, {
      width: '420px',
      data: {
        mode: 'range',
        label,
        startValue: (this.search.filters as any)[startKey] ?? null,
        endValue: (this.search.filters as any)[endKey] ?? null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!this.search || result === undefined) {
        return;
      }

      if (result === null) {
        delete (this.search.filters as any)[startKey];
        delete (this.search.filters as any)[endKey];
      } else {
        (this.search.filters as any)[startKey] = result.start;
        (this.search.filters as any)[endKey] = result.end;
      }

      this.updateFilterUi();
      this.updateSearchFilters();
    });
  }

  formatDateChip(
    start?: string | Date | null,
    end?: string | Date | null
  ): string {
    if (!start && !end) {
      return '';
    }

    const startLabel = start ? this.formatShortDate(start) : '';
    const endLabel = end ? this.formatShortDate(end) : '';

    if (startLabel && endLabel && startLabel === endLabel) {
      return startLabel;
    }

    if (startLabel && endLabel) {
      return `${startLabel} - ${endLabel}`;
    }

    return startLabel || endLabel;
  }

  formatShortDate(value: string | Date): string {
    const date = value instanceof Date
      ? value
      : new Date(`${value}T00:00:00`);

    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
    });
  }

  sortBy(active: string, direction: 'asc' | 'desc'): void {
    this.sort.active = active;
    this.sort.direction = direction;
    this.sort.sortChange.emit({
      active,
      direction,
    });
  }

  clearSort(): void {
    this.sort.active = '';
    this.sort.direction = '';
    this.sort.sortChange.emit({
      active: '',
      direction: '',
    });
  }
  
}